import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  _addToPenta,
  _createAvatar,
  _createPenta,
  _deleteAvatar,
  _deleteFromPenta,
  _deletePenta,
  _follow,
  _getPenta,
  _invitedBy,
  _setIsMe,
  _unfollow,
  _updateAvatar,
  convertGMTtoDateTime,
  parseAvatar,
  parseDesignInPersonas,
  parseNewDesign
} from '../components/utilsDesign';
// import data_bak_17_feb_2024 from '../static/data_bak_17_feb_2024.json';
// import cached_04_mar_2024 from '../static/cached_04_mar_2024.json';
import { PreloaderHD } from '../components/UiKit/PreloaderHD';
import { _getCity, _updateMyCity, withCityLocation } from '../components/utilsGeo';
import {
  _getGroups,
  _getInvitations,
  _groupCreate,
  _groupDelete,
  _groupLeave,
  _groupUpdate,
  _removeUserFromGroup
} from '../components/utilsGroup';
import { cacheClear, cacheDelete, cacheRead, cacheWrite } from '../components/utilsCacheDB';
import { _updatePenta, applyPentaMemberOrder, getPentaDesign } from '../components/utilsPenta';
import { appNavigate, getLocal, isViewingAvatar, isViewingPenta, setLocal } from '../components/utilsSocial';
import { API_URL } from '../apiUrl';
import { PAGINATE_DEFAULT } from './stateContext';


export const LOAD_BY_DEFAULT = 1000;
export const LIST_PAGE_SIZE_DEFAULT = PAGINATE_DEFAULT;

export const listPageSize = () => {
  const stored = Number(getLocal('paginate'));
  return Number.isFinite(stored) && stored > 0 ? stored : PAGINATE_DEFAULT;
};

/** Keep only fields SocialFilters needs — avoids holding 300 full HD designs in RAM. */
export const toStatsOnlyPersona = (one) => {
  if (!one) return one;
  if (one._statsOnly) return one;
  let design = one?.avatar?.design;
  if (typeof design === 'string') {
    try {
      design = JSON.parse(design);
    } catch {
      design = null;
    }
  }
  const profile = design?.profile;
  return {
    _statsOnly: true,
    ...(typeof one._listIndex === 'number' ? { _listIndex: one._listIndex } : {}),
    user: one.user ? { token: one.user.token } : undefined,
    avatar: one.avatar ? {
      token: one.avatar.token,
      name: one.avatar.name,
      dob: one.avatar.dob,
      image: one.avatar.image,
      design: design ? {
        id: design.id || design.meta?.personality,
        type: design.type,
        profile: Array.isArray(profile) ? [profile[0], profile[1]] : profile,
      } : undefined,
      aggr: one.avatar?.aggr?.city?.location
        ? { city: { location: one.avatar.aggr.city.location } }
        : undefined,
    } : undefined,
  };
};

export const isStatsOnlyPersona = (item) => Boolean(item?._statsOnly);

const mergeAtSkip = (prev = [], incoming = [], skip = 0) => {
  const next = prev.slice();
  incoming.forEach((item, i) => {
    next[skip + i] = item;
  });
  return next;
};

/** Stats walk must not clobber a fully hydrated persona with a slim placeholder. */
const mergeAtSkipPreferFull = (prev = [], incoming = [], skip = 0) => {
  const next = prev.slice();
  incoming.forEach((item, i) => {
    const idx = skip + i;
    const existing = next[idx];
    if (existing && !existing._statsOnly && item?._statsOnly) return;
    next[idx] = item;
  });
  return next;
};

const yieldToMain = () => new Promise((resolve) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => resolve(), { timeout: 120 });
  } else {
    setTimeout(resolve, 32);
  }
});


// const OFFLINE = true;
const OFFLINE = false;

const SocialContext = React.createContext();

const ME_STORAGE_KEY = 'me';
const API_URL_STORAGE_KEY = 'api_url';

const clearMeCacheIfApiChanged = () => {
  const storedApiUrl = getLocal(API_URL_STORAGE_KEY);
  if (storedApiUrl && storedApiUrl !== API_URL) {
    setLocal(ME_STORAGE_KEY, '');
    cacheClear();
  }
  if (API_URL) {
    setLocal(API_URL_STORAGE_KEY, API_URL);
  }
};


function SocialProvider({ children }) {

  const [summary, setSummary] = useState(
    // getLocal('summary') ? getLocal('summary') : null
    // OFFLINE ? cached_04_mar_2024 : null
  );
  const [cachedDesigns, setCachedDesigns] = useState(
    // OFFLINE ? cached_04_mar_2024 : null
  );



  const [myCity, setMyCityInner] = useState({});
  const [myCities, setMyCities] = useState([]);

  const setMyCity = (cityObj) => {
    setMyCityInner(cityObj || {})
  }

  const processMe = (me, summary) => {
    let newMe = {
      ...me,
      user: {
        ...me.user,
        isMe: true
      },
      avatar: {
        ...me.avatar,
        isMe: true,
        my: true,
      },
      // avatar: me.avatar.token,
      // avatar: {
      //   ...me.avatar,
      //   isMe: true
      // },
      isMe: true,
    }
    if (summary) {
      newMe.followers = summary.followers;
      newMe.followings = summary.followings;
    }
    return newMe;
  }

  const localMe = useMemo(() => {
    clearMeCacheIfApiChanged();
    return getLocal(ME_STORAGE_KEY) || null;
  }, []);
  const [me, setMe] = useState(localMe);
  const [cachedUsers, setCachedUsers] = useState(() =>
    localMe ? { [localMe.user.token]: processMe(localMe) } : {}
  );

  // Social lists are cached per user so a re-open of the app can paint the
  // Network / Bodygraphs / Synergy tabs before the API answers.
  const userTokenRef = useRef(localMe?.user?.token || null);
  const listLoadedFromApiRef = useRef({});
  const hydratedRef = useRef(null);
  const statsWalkGenRef = useRef({ followings: 0, followers: 0 });

  useEffect(() => {
    userTokenRef.current = me?.user?.token || null;
  }, [me?.user?.token]);

  const cacheKey = (list) => (userTokenRef.current ? `${userTokenRef.current}:${list}` : '');

  /** Snapshots a freshly fetched payload before the parsers mutate it in place. */
  const cacheList = (list, payload) => {
    listLoadedFromApiRef.current[list] = true;
    cacheWrite(cacheKey(list), payload);
  };

  /**
   * Drops the snapshot after a mutation we only applied locally, so the next cold
   * start waits for the API instead of painting a list we know is out of date.
   */
  const invalidateList = (list) => {
    cacheDelete(cacheKey(list));
  };
  // const [me, setMe] = useState(getLocal('me') ? getLocal('me') : null);
  // const [cachedUsers, setCachedUsers] = useState(
  //   {}
  //   // getLocal('me') ? { [getLocal('me').user.token]: getLocal('me') } : {}
  // );
  
  useEffect(() => {
    // console.log('cachedUsers = ', cachedUsers);
  }, [cachedUsers]);

  const [avatars, setAvatars] = useState();
  const [groups, setGroups] = useState();

  const [friends, setFriends] = useState([]);
  const [followings, setFollowings] = useState();
  const [followers, setFollowers] = useState();
  // First page ready (UI can stop waiting). `Complete` = entire list is in memory.
  const [followingsHydrated, setFollowingsHydrated] = useState(false);
  const [followersHydrated, setFollowersHydrated] = useState(false);
  const [followingsComplete, setFollowingsComplete] = useState(false);
  const [followersComplete, setFollowersComplete] = useState(false);

  const [fetchingSummary, setFetchingSummary] = useState(false);
  const [meReady, setMeReady] = useState(false);


  const [pentas, setPentas] = useState();
  const [pentasPersons, setPentasPersons] = useState({});


  


  const [publicUser, setPublicUser] = useState();



  const addCityHistory = (cityObj) => {
    if (!cityObj || !cityObj.id) return;
    const cities = myCities.filter(one => one.id !== cityObj.id)
    setMyCities([cityObj, ...cities])
  }




  // LOADINGS
  const [updatingAva, setUpdatingAva] = useState();
  const [updatingPenta, setUpdatingPenta] = useState();
  const [creatingAva, setCreatingAva] = useState(false);
  const [creatingPenta, setCreatingPenta] = useState(false);
  const [loadingDesign, setLoadingDesign] = useState(false);
  const [deletingAvatar, setDeletingAvatar] = useState([]);
  const [followingUser, setFollowingUser] = useState([]);


  const [cityPeople, setCityPeople] = useState();
  const [geoPeople, setGeoPeople] = useState({});

  const [deletingPenta, setDeletingPenta] = useState([]);

  useEffect(() => {
    if (!me?.user) return
    const aggrCity = me.user.aggr?.city
    if (aggrCity?.id && aggrCity.id === me.user.city_id) {
      setMyCityInner(aggrCity)
    } else if (me.user.city_id) {
      _getCity(me.user.city_id, setMyCityInner)
    } else {
      setMyCityInner({})
    }
  }, [me?.user?.city_id, me?.user?.aggr?.city?.id])



  useEffect(() => {
    if (me && me.user && me.user.aggr.city) {
      fetchData(`geo/users/${me.user.city_id}`, (pep = []) => {
        pep = parseDesignInPersonas(pep)
        setCityPeople(pep)
      })

      //TODO: сделать людей по координатам
      // const cityObj = me.user.aggr.city;
      // fetchData(`geo/close/?lat=${cityObj.location.lat}&lon=${cityObj.location.lon}&distance=100}`, (cityP) => {
      //   cityP = parseDesignInPersonas(cityP)
      //   if (cityP.length) cacheCityPeople(cityObj.id, cityP)
      //   callback(cityP)
      // })
    }
  }, [me])


  const cacheCityPeople = (cityId, newPeople = []) => {
    let myCity = {}
    newPeople = parseDesignInPersonas(newPeople)
    myCity[cityId] = newPeople;
    setGeoPeople((prev = {}) => {
      // console.log('prev = ', prev);
      let newOld = JSON.parse(JSON.stringify(prev))
      function removeItemsByUserToken(data, tokenToRemove) {
        Object.keys(data).forEach(key => {
          data[key] = data[key].filter(item => item.user.token !== tokenToRemove);
        });
        return data;
      }
      newOld = Object.keys(prev).length ? removeItemsByUserToken(newOld, me.user.token) : prev;

      // return ({ ...newOld, ...newPeople })
      if (newPeople.length) newOld[cityId] = newPeople;
      // console.log('newOld = ', newOld);
      return newOld;
    })
  }

  useEffect(() => {
    if (me && cityPeople && me.user.city_id) {
      cacheCityPeople(me.user.city_id, cityPeople)
    }
  }, [cityPeople, me])

  useEffect(() => {
    // console.log('geoPeople = ', geoPeople);
  }, [geoPeople])


  const getCityPeople = (cityObj, callback = () => void (0)) => {
    if (!cityObj || !cityObj.id || !cityObj.location) {
      callback()
      return
    }
    if (geoPeople?.[cityObj.id]) {
      callback(geoPeople[cityObj.id])
    } else {
      // fetchData(`geo/users/${cityObj.id}`, (cityP) => {
      //   cityP = parseDesignInPersonas(cityP)
      //   if (cityP.length) cacheCityPeople(cityObj.id, cityP)
      //   callback(cityP)
      // })
      //TODO: сделать людей по координатам
      fetchData(`search/users/?lat=${cityObj.location.lat}&lon=${cityObj.location.lon}&distance=100`, (cityP) => {
        cityP = parseDesignInPersonas(cityP)
        if (cityP.length) cacheCityPeople(cityObj.id, cityP)
        callback(cityP)
      })


    }
  }




  useEffect(() => {

    // let up;
    // if (updatingAva) {
    //   let a = getAvatar(updatingAva);
    //   if (a.dob) {
    //     up = convertDobToId(a['dob'])
    //   }
    // } else { up = null }

    // if (
    //   up
    // ) {
    //   setUpdatingAva(false)
    // }
  }, [cachedDesigns])

  useEffect(() => {
    // console.log('summary = ', summary);
  }, [summary])

  useEffect(() => {
    // console.log('pentas = ', pentas);
  }, [pentas])

  useEffect(() => {
    // console.log('me = ', me);
  }, [me])

  // useEffect(() => {
  //   console.log('newPentaPersonas = ', pentasPersons);
  // }, [pentasPersons])

  useEffect(() => {
    // console.log('avatars = ', avatars);
  }, [avatars])

  useEffect(() => {
    // console.log('friends = ', friends);
  }, [friends])

  useEffect(() => {
    // console.log('followings = ', followings);
  }, [followings])

  useEffect(() => {
    // console.log('followers = ', followers);
  }, [followers])




  useEffect(() => {
  }, [updatingAva]);



  // useEffect(() => {
  //   if (
  //     typeof friends === 'object'
  //     && typeof avatars === 'object'
  //     && !cachedDesigns
  //   ) {
  //     // console.log('friends = ', friends);
  //     // console.log('avatars = ', avatars);
  //     let allAvatars = []
  //     friends.forEach(one => allAvatars.push(one.avatar))
  //     avatars.forEach(one => allAvatars.push(one.avatar))
  //     // cacheNewDesigns(allAvatars)
  //   }
  // }, [friends, avatars, followings, followers, cachedDesigns])







  // const fetchPentas = (pentasList = []) => {
  //   pentasList.map(onePenta => fetchData(`penta/avatars/${onePenta.token}`, (pentaData) => processPenta(onePenta.token, pentaData))
  //   )
  // }


  // const processPenta = (pentaId, pentaAvatars = []) => {
  //   const newPentasList = JSON.parse(JSON.stringify(pentasList))
  //   const findIndex = newPentasList.findIndex(one => one.token === pentaId)
  //   if (findIndex > -1) {
  //     newPentasList[findIndex]['avatars'] = pentaAvatars
  //   }
  //   setPentas(newPentasList)
  // }


  const enrichPenta = (penta) => {
    const ordered = applyPentaMemberOrder(penta);
    ordered.members = parseDesignInPersonas((ordered.members || []).map(withCityLocation));
    ordered.pentaDesign = getPentaDesign(ordered);
    ordered.my = true;
    Object.assign(penta, ordered);
    return penta;
  };

  const fetchPentas = (pentasList = []) => {

    pentasList.forEach(onePenta => {
      enrichPenta(onePenta)
    })
    // console.log('pentasList = ', pentasList);
    
    setPentas(pentasList)
    // Use Promise.all to wait for all fetchData promises to resolve
    // Promise.all(pentasList.map(onePenta =>
    //   fetchDataAsync(`penta/avatars/${onePenta.token}`).then(pentaData =>
    //     ({ token: onePenta.token, avatars: pentaData })
    //   )
    // ))
    //   .then(results => {
    //     // Once all promises are resolved, update the state
    //     let pentasWithAvatars = JSON.parse(JSON.stringify(pentasList))
    //     results.forEach(({ token, avatars }) => {
    //       const findIndex = pentasWithAvatars.findIndex(one => one.token === token);
    //       if (findIndex > -1) {
    //         pentasWithAvatars[findIndex]['avatars'] = avatars;
    //       }
    //     })
    //     setPentas(pentasWithAvatars);
    //   })
    //   .catch(error => {
    //     // Handle any errors
    //     console.error('Error fetching data:', error);
    //   });
  }


  // const updateLocalSummary = (what, newWhat) => {
  //   const newSummary = JSON.parse(JSON.stringify(summary))
  //   newSummary[what] = newWhat;
  //   setSummary(processSummary(newSummary))
  // }

  const processAvatars = (avatars = []) => {
    avatars = avatars.map(one => ({
      user: null,
      avatar: parseAvatar({
        ...one,
        my: true,
        isMe: me.avatar.token === one.token ? true : false
      })
    }))
    // avatars = avatars.filter(one => one.avatar.token !== me.avatar.token)
    return avatars;
  }

  const fetchDataAsync = (endpoint) => {
    return fetch(`${API_URL}/api/${endpoint}`) // Assuming you're using localhost
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      });
  };






  const convertDobToId = (dob) => {
    const { date, time } = convertGMTtoDateTime(dob);
    let d = date.split('-');
    let t = time.split(':')
    const id = `${d[0]}${d[1]}${d[2]}${t[0]}${t[1]}01`
    return id;
  }

  const getDesign1 = (dob, token) => {
    // setLoadingDesign(token)
    if (!dob) return;
    let ret = cachedDesigns[convertDobToId(dob)]
    return ret
  }


  const getDesign = (avatar) => {
    // setLoadingDesign(avatar.token)
    if (!avatar.dob) return;
    let ret = cachedDesigns[convertDobToId(avatar.dob)]
    return ret
  }

  const getPenta = (token, callback = () => void (0), callbackFail = () => void (0)) => {
    const local = pentas?.find(one => one.token === token)
    if (local) {
      const ordered = applyPentaMemberOrder(local);
      ordered.pentaDesign = getPentaDesign(ordered)
      ordered.my = true;
      callback(ordered)
      return
    }

    _getPenta(token, (penta) => {
      const ordered = applyPentaMemberOrder(penta);
      ordered.pentaDesign = getPentaDesign(ordered)
      ordered.my = false;
      callback(ordered)
    }, callbackFail)
  }



  const fetchPenta = (pentaId, callback) => {
    fetch(`${API_URL}/api/penta/${pentaId}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json();
      })
      .then(data => {
        callback(data)
      })
      .catch(error => {
        console.error("Error fetching: ", error);
      });
  }


  const fetchPentaAvatars = (pentaId, callback) => {
    fetch(`${API_URL}/api/penta/avatars/${pentaId}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json();
      })
      .then(data => {
        callback(data)
        // cacheNewDesigns(data)
        // console.log('data = ', data);
      })
      .catch(error => {
        console.error("Error fetching: ", error);
      });
  }


  const fetchAllUsers = (callback) => {
    fetch(`${API_URL}/api/public/users/8`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json();
      })
      .then(data => {
        callback(data)
        // cacheNewDesigns(data)
        // console.log('data = ', data);
      })
      .catch(error => {
        console.error("Error fetching: ", error);
      });
  }


  const cacheNewDesigns = (data = [], dontEraseCache) => {
    // if (!dontEraseCache) setCachedDesigns({})
    // let dobs = ''
    // data.forEach((one, ind, array) => {
    //   if (one.dob) {
    //     const { date, time, timezone = 'GMT' } = { ...convertGMTtoDateTime(one['dob']) }
    //     dobs += `${date}|${time}|${timezone}${ind < array.length - 1 ? ',' : ''}`
    //     // getDesign(date, time, timezone, 'London', newDesign => cacheDesign(one['dob'], newDesign))
    //   }
    // })
    // // console.log('dobs = ', dobs);
    // _getDesignArray(dobs, (data) => {
    //   if (data) {
    //     setCachedDesigns((prevCache = {}) => ({ ...prevCache, ...processDesigns(data) }))
    //   }
    //   setUpdatingAva(false)
    // })
  }


  const processDesigns = (array = []) => {
    const ret = {}
    array.forEach(one => {
      ret[one['id']] = { ...one }
    })
    return ret
  }








  const filterDuplicatesByToken = (originalArray = [], newArray = [], compareBy = 'user') => {
    if (!originalArray.length) return newArray;
    const existingTokens = [...new Set(originalArray.map(item => item[compareBy]?.token))];
    return newArray.filter(item => !existingTokens.includes(item[compareBy]?.token))
  }


  
  /** A full refresh (first page + replace) is what we mirror into the offline cache. */
  const isFullListLoad = (skip, replace) => Boolean(replace) && !skip

  const loadFollowings = (skip, limit, callback = () => void (0), replace = false) => {
    fetchData(`me/followings${typeof skip != 'undefined' ? `?skip=${skip}` : ''}${typeof limit != 'undefined' && typeof skip != 'undefined' ? `&limit=${limit}` : ''}`, 
      (loadedFollowings) => {
        const base = skip || 0;
        loadedFollowings = parseDesignInPersonas(loadedFollowings).map((item, i) => ({
          ...item,
          _listIndex: base + i,
          _statsOnly: false,
        }));
        setFollowings((prevFollowings = []) => {
          if (replace) return loadedFollowings;
          return mergeAtSkip(prevFollowings, loadedFollowings, base);
        })
      callback(loadedFollowings)
    })
  }

  const loadFollowers = (skip, limit, callback = () => void (0), replace = false) => {
    fetchData(`me/followers${typeof skip != 'undefined' ? `?skip=${skip}` : ''}${typeof limit != 'undefined' && typeof skip != 'undefined' ? `&limit=${limit}` : ''}`, 
      (loadedFollowers) => {
        const base = skip || 0;
        loadedFollowers = parseDesignInPersonas(loadedFollowers).map((item, i) => ({
          ...item,
          _listIndex: base + i,
          _statsOnly: false,
        }));
        setFollowers((prevFollowers = []) => {
          if (replace) return loadedFollowers;
          return mergeAtSkip(prevFollowers, loadedFollowers, base);
        })
        callback(loadedFollowers)
      })
  }

  /** First page = full personas for paint; remainder = slim stats-only rows (chunked). */
  const loadFollowingsAll = (callback = () => void (0), totalOverride) => {
    const pageSize = listPageSize();
    const total = typeof totalOverride === 'number'
      ? totalOverride
      : (me?.user?.aggr?.followings_count || 0);
    const walkId = ++statsWalkGenRef.current.followings;
    setFollowingsHydrated(false);
    setFollowingsComplete(false);
    loadFollowings(0, pageSize, (firstPage = []) => {
      if (walkId !== statsWalkGenRef.current.followings) return;
      listLoadedFromApiRef.current.followings = true;
      const got = Array.isArray(firstPage) ? firstPage.length : 0;
      setFollowingsHydrated(true);

      const finish = (acc) => {
        if (walkId !== statsWalkGenRef.current.followings) return;
        cacheList('followings', (acc || []).map(toStatsOnlyPersona));
        setFollowingsComplete(true);
        callback();
      };

      if (total <= got) {
        finish(firstPage);
        return;
      }

      // Keep a local sparse list for the offline cache; React state gets the same merges.
      let skip = got;
      let built = firstPage.slice();
      const walk = () => {
        if (walkId !== statsWalkGenRef.current.followings) return;
        if (skip >= total) {
          finish(built);
          return;
        }
        const limit = Math.min(pageSize, total - skip);
        fetchData(
          `me/followings?skip=${skip}&limit=${limit}`,
          (loaded = []) => {
            if (walkId !== statsWalkGenRef.current.followings) return;
            const batch = Array.isArray(loaded) ? loaded : [];
            // Do not run parseDesignInPersonas here — that allocates full HD graphs.
            const slim = batch.map((item, i) => ({
              ...toStatsOnlyPersona(item),
              _listIndex: skip + i,
            }));
            built = mergeAtSkipPreferFull(built, slim, skip);
            setFollowings((prev = []) => mergeAtSkipPreferFull(prev, slim, skip));
            skip += batch.length;
            if (!batch.length) {
              finish(built);
              return;
            }
            yieldToMain().then(walk);
          }
        );
      };
      yieldToMain().then(walk);
    }, true);
  }

  const loadFollowersAll = (callback = () => void (0), totalOverride) => {
    const pageSize = listPageSize();
    const total = typeof totalOverride === 'number'
      ? totalOverride
      : (me?.user?.aggr?.followers_count || 0);
    const walkId = ++statsWalkGenRef.current.followers;
    setFollowersHydrated(false);
    setFollowersComplete(false);
    loadFollowers(0, pageSize, (firstPage = []) => {
      if (walkId !== statsWalkGenRef.current.followers) return;
      listLoadedFromApiRef.current.followers = true;
      const got = Array.isArray(firstPage) ? firstPage.length : 0;
      setFollowersHydrated(true);

      const finish = (acc) => {
        if (walkId !== statsWalkGenRef.current.followers) return;
        cacheList('followers', (acc || []).map(toStatsOnlyPersona));
        setFollowersComplete(true);
        callback();
      };

      if (total <= got) {
        finish(firstPage);
        return;
      }

      let skip = got;
      let built = firstPage.slice();
      const walk = () => {
        if (walkId !== statsWalkGenRef.current.followers) return;
        if (skip >= total) {
          finish(built);
          return;
        }
        const limit = Math.min(pageSize, total - skip);
        fetchData(
          `me/followers?skip=${skip}&limit=${limit}`,
          (loaded = []) => {
            if (walkId !== statsWalkGenRef.current.followers) return;
            const batch = Array.isArray(loaded) ? loaded : [];
            const slim = batch.map((item, i) => ({
              ...toStatsOnlyPersona(item),
              _listIndex: skip + i,
            }));
            built = mergeAtSkipPreferFull(built, slim, skip);
            setFollowers((prev = []) => mergeAtSkipPreferFull(prev, slim, skip));
            skip += batch.length;
            if (!batch.length) {
              finish(built);
              return;
            }
            yieldToMain().then(walk);
          }
        );
      };
      yieldToMain().then(walk);
    }, true);
  }


  const loadAvatars = (skip, limit, callback = () => void (0), replace = false) => {
    fetchData(`me/avatars${typeof skip !== 'undefined' ? `?skip=${skip}` : ''}${typeof limit !== 'undefined' && typeof skip !== 'undefined' ? `&limit=${limit}` : ''}`, 
      (loadedAvatars) => {
        // console.log('loadedAvatars = ', loadedAvatars);
        if (isFullListLoad(skip, replace)) cacheList('avatars', loadedAvatars)
        setAvatars((prevAvatars = []) => {
          const newAvatars = processAvatars(loadedAvatars);
          const allAvatars = replace ? newAvatars : [...prevAvatars, ...filterDuplicatesByToken(prevAvatars, newAvatars, 'avatar')];
          callback(allAvatars)
          // console.log('allAvatars123 = ', allAvatars);
          return allAvatars;
        })
    })
  }



  const loadPentas = () => fetchData('me/pentas', (loadedPentas) => {
    cacheList('pentas', loadedPentas)
    fetchPentas(loadedPentas)
  })
  const mergeGroupsWithInvitations = (loadedGroups = [], invitations = []) => {
    const memberTokens = new Set((loadedGroups || []).map(group => group.token))
    const invitationGroups = (invitations || []).filter(group => !memberTokens.has(group.token))
    return [...(loadedGroups || []), ...invitationGroups]
  }

  const loadGroups = () => fetchData('me/groups', (loadedGroups = []) => {
    _getInvitations((invitations = []) => {
      setGroups(mergeGroupsWithInvitations(loadedGroups, invitations))
    })
  })


  const setIsMe = ({ user, avatar }) => {
    let avatarToken = avatar ? avatar.token : ''
    if (avatarToken) {
      _setIsMe(avatarToken, fetchData('me', (me) => {
        let newMe = processMe(me)
        // console.log('newMe = ', newMe);
        setLocal('me', newMe)
        setMe(newMe)
      }))
    } else { console.log('No avatar token') }
  }



  const patchAvatarInList = (token, patch) => {
    setAvatars((prevAvatars = []) =>
      (prevAvatars || []).map((one) =>
        one.avatar?.token === token
          ? {
              ...one,
              avatar: {
                ...one.avatar,
                ...patch,
                my: true,
                isMe: me?.avatar?.token === token,
              },
            }
          : one
      )
    )
  }

  const updateAvatar = (newData, callback = () => void (0)) => {

    if (newData.dob) {
      setAvatars((prevAvatars = []) => {
        // const newAvatars = JSON.parse(JSON.stringify(prevAvatars))
        let newAvatars = []
        prevAvatars = prevAvatars.forEach(one => {
          one = JSON.parse(JSON.stringify(one))
          // one.avatar.design = (one.avatar.design && one.avatar.design['meta']) ? parseNewDesign(one.design) : default_design
          if (one.token !== newData.token) {
            newAvatars.push(one)
          } else {
            one.avatar.dob = null;
            newAvatars.push(one)
          }
        })
        newAvatars = newAvatars.sort((a, b) => {
          if (a.avatar.token === me.avatar.token) return -1;
          if (b.avatar.token === me.avatar.token) return 1;
        })
        return newAvatars
      })
      // cacheNewDesigns([newData], 'dontEraseCache')
    } else if (newData.token) {
      // Optimistic patch for name/image/etc so open pages (info) don't keep stale data
      // while waiting for loadAvatars to finish.
      patchAvatarInList(newData.token, newData)
    }
    if (newData.token === me.avatar.token) {
      setMe((prevMe = {}) => {
        const newMe = {
          ...prevMe,
          avatar: {
            ...prevMe.avatar,
            ...newData
          }
        }
        return newMe
      })
    }

    const afterUpdate = (newReceivedData) => {
      patchAvatarInList(newReceivedData.token, newReceivedData)
      if (!newData.dob) {
        setUpdatingAva(false);
      }
      if (newData.token === me.avatar.token) {
        // fetchData('me', (me) => setMe(processMe(me)));
        let newMe = JSON.parse(JSON.stringify(me))
        newMe.avatar = { ...newMe.avatar, ...newReceivedData };
        newMe = processMe(newMe)
        setMe(newMe)
        setLocal('me', newMe)
        let newU = {}
        newU[me.avatar.token] = newMe;
        setCachedUsers(prev => ({ ...prev, ...newU }))
      }
      loadAvatars(0, LOAD_BY_DEFAULT, () => 
        setTimeout(
          setUpdatingAva(false), 1000
        ), 'replace'
      )
    }

    if (newData.token && newData) {
      setUpdatingAva(newData.token)
      _updateAvatar(newData, (newAvatar) => {
        // data.design = parseNewDesign(newAvatar.design)
        newAvatar = parseAvatar(newAvatar)
        afterUpdate(newAvatar)
        callback(newAvatar)
      })
      // if (newData.dob) {
      // cacheNewDesigns([newData], 'dontEraseCache')
      // }
    }

  }


  const createAvatar = (avatarData = {}, callback = () => void (0)) => {

    setCreatingAva(true)
    // callback(0)

    const fetchCallback = (newAvatar) => {
      newAvatar.dob = null;
      newAvatar = processAvatars([newAvatar])
      setAvatars((prevAvatars = []) => [...prevAvatars, ...newAvatar])
      invalidateList('avatars')
      callback(newAvatar[0].avatar)
      setCreatingAva(false)
    }
    _createAvatar(avatarData, fetchCallback)
  }



  const createGroup = (groupData = {}, callback = () => void (0)) => {
    // setCreatingAva(true)
    const fetchCallback = (newGroup, error) => {
      if (!newGroup) {
        callback(null, error)
        return
      }
      setGroups((prev = []) => [...prev, newGroup])
      callback(newGroup)
      setCreatingAva(false)
    }
    _groupCreate(groupData, fetchCallback)
  }

  const updateGroup = (groupData = {
    token: '',
    name: '',
    description: ''
  }, callback = () => void (0)) => {
    // setCreatingAva(true)
    const fetchCallback = (groupData) => {
      setGroups((prev = []) => {
        const newG = JSON.parse(JSON.stringify(prev))
        const index = groups.findIndex(group => group.token === groupData.token)
        newG[index] = {
          ...newG[index],
          ...groupData
        }
        return newG
      })
    }
    fetchCallback(groupData)
    _groupUpdate(groupData, callback)
  }

  const getGroups = (callback = () => void (0)) => {
    const fetchCallback = (newGroups = []) => {
      _getInvitations((invitations = []) => {
        const merged = mergeGroupsWithInvitations(newGroups, invitations)
        setGroups(merged)
        callback(merged)
      })
    }
    _getGroups(fetchCallback)
  }


  const deleteGroup = (token = '', callback = () => void (0)) => {
    // setCreatingAva(true)
    const fetchCallback = (token) => {
      setGroups(prev => [...prev.filter(group => group.token !== token)])
    }
    fetchCallback(token)
    _groupDelete(token)
  }

  const leaveGroup = (token = '', callback = () => void (0)) => {
    setGroups(prev => [...(prev || []).filter(group => group.token !== token)])
    _groupLeave(token, callback)
  }

  const removeUserFromGroup = (groupToken = '', userToken = '', callback = () => void (0)) => {
    _removeUserFromGroup(groupToken, userToken, callback)
  }

  const createPenta = (avatars = [], callback = () => void (0)) => {
    setCreatingPenta(true)
    const afterCreate = (newPenta) => {
      newPenta.members = newPenta.members ? parseDesignInPersonas(newPenta.members) : [];
      enrichPenta(newPenta);
      setPentas((prevPentas = []) => [...prevPentas, newPenta])
      invalidateList('pentas')
      setCreatingPenta(false)
      callback(newPenta)
    }
    _createPenta({ avatars }, afterCreate)
  }

  const createPentaWithMe = (callback = () => void (0)) => {
    const avatarToken = me?.avatar?.token;
    if (!avatarToken) {
      console.error('createPentaWithMe: no avatar token on me');
      return;
    }
    setCreatingPenta(true)
    callback(0)
    const fetchCallback = (newPenta) => {
      if (!newPenta?.members?.length) {
        console.error('createPentaWithMe: penta created without members, avatar not found:', avatarToken);
        setCreatingPenta(false)
        loadPentas()
        return;
      }
      newPenta.members = parseDesignInPersonas(newPenta.members);
      enrichPenta(newPenta);
      setPentas((prevPentas = []) => [...prevPentas, newPenta])
      invalidateList('pentas')
      callback()
      setCreatingPenta(false)
    }
    _createPenta({ avatars: [avatarToken] }, fetchCallback)
  }

  const deleteAvatar = ({ avatar }, callback = () => void (0)) => {
    const shouldRedirect = isViewingAvatar(avatar, me);
    setDeletingAvatar((prev) => [...prev, avatar.token])
    invalidateList('avatars')
    _deleteAvatar(avatar.token, () => {
      callback();
      loadAvatars(0, LOAD_BY_DEFAULT, () => {
        setDeletingAvatar((prev) => prev.filter(token => token !== avatar.token))
        if (shouldRedirect && me?.user?.token) {
          appNavigate(`/u/${me.user.token}`);
        }
      }, true);
    })
  }

  const deletePenta = (arg) => {
    // Accept either a token string, a penta object, or { penta }
    const penta = arg && typeof arg === 'object' && 'penta' in arg ? arg.penta : arg;
    const token = typeof penta === 'string' ? penta : penta?.token;
    if (!token) return;

    // If we're currently viewing this penta (/p/:token), leave the page before
    // it re-renders against the now-deleted penta and crashes.
    if (isViewingPenta(token)) {
      appNavigate('/');
    }

    setDeletingPenta((prev) => [...prev, token])
    invalidateList('pentas')
    const newPentas = (pentas || []).filter(one => one.token !== token);
    setPentas(newPentas)
    // updateLocalSummary('pentas', newPentas)
    _deletePenta(token, () => {
      setDeletingPenta((prev) => prev.filter(one => one !== token))
      loadPentas()
    })
  }

  const addToPenta = (pentaId = '', newAvatarId = '') => {
    if (!pentaId || !newAvatarId) {
      return;
    }
    const newPentaCallback = (newPenta) => {
      const newPentas = JSON.parse(JSON.stringify(pentas));
      const pentaIndex = newPentas.findIndex(one => one.token === newPenta.token)

      newPenta = {
        ...newPenta,
        'members': newPenta.members.map(one => {
          const newU = {
            ...one,
            avatar: {
              ...one.avatar,
              isMe: one.user && one.user.token === me.user.token,
            },
          }
          if (one.user) {
            newU.user = {
              ...one.user,
              isMe: one.user.token === me.user.token,
            }
          } else {
            newU.user = null
          }
          return newU
        })
      }

      if (pentaIndex < 0) {
        newPenta.members = parseDesignInPersonas(newPenta.members)
        enrichPenta(newPenta);
        newPentas.push(newPenta)
      }
      else {
        newPenta.members = parseDesignInPersonas(newPenta.members).reverse()
        enrichPenta(newPenta);
        newPentas[pentaIndex] = newPenta;
      }
      setPentas(newPentas)
      invalidateList('pentas')
    }
    _addToPenta(pentaId, newAvatarId, newPentaCallback)
  }

  const deleteFromPenta = (pentaId, avatarId, callback = () => void (0)) => {
    if (!pentaId || !avatarId) {
      return;
    }
    const afterDeleteCallback = (newPenta) => {
      const newPentas = JSON.parse(JSON.stringify(pentas));
      newPenta.members = parseDesignInPersonas(newPenta.members)
      const pentaIndex = newPentas.findIndex(one => one.token === newPenta.token)
      if (pentaIndex < 0) {
        enrichPenta(newPenta);
        newPentas.push(newPenta)
      }
      else {
        newPenta.members = newPenta.members.reverse()
        enrichPenta(newPenta);
        newPentas[pentaIndex] = newPenta;
      }
      setPentas(newPentas)
      invalidateList('pentas')
      callback(newPenta)
    }
    _deleteFromPenta(pentaId, avatarId, afterDeleteCallback)
  }







  const getUserSocial = async (userId, what, len) => {
    try {
      // Assuming fetchData returns a promise. If fetchData uses callbacks,
      // you would need to wrap it in a new Promise as shown below.
      const newUser = await fetchDataPromise(`user/${userId}`);

      let processedUser = newUser;
      if (me.user.token === userId) {
        processedUser = processMe(newUser);
      }

      const newU = { [userId]: processedUser };

      if (what && processedUser[what]) {
        return len ? processedUser[what].length : processedUser[what]
      }

      return processedUser;
    } catch (error) {
      console.error("Error fetching user:", error);
      // Handle error (e.g., by re-throwing it or returning a default value)
      throw error; // Or return null;
    }
  };

  const refreshMe = (callback = () => void (0)) => {
    fetchData('me', (fetchedMe) => {
      const newMe = processMe(fetchedMe)
      setMe(newMe)
      setLocal(ME_STORAGE_KEY, newMe)
      callback(newMe)
    })
  }

  const updateMyCity = (cityId, callback = () => void (0), cityObj = null) => {
    setUpdatingAva(me.user.token)
    const applyCity = (obj) => {
      if (obj?.city) {
        setMyCity(obj)
      }
    }
    if (cityObj) applyCity(cityObj)
    else if (cityId) _getCity(cityId, applyCity)
    _updateMyCity(cityId, (mewMe) => {
      mewMe = processMe(mewMe)
      setMe(mewMe)
      setLocal('me', mewMe)
      setUpdatingAva()
      callback()
    })
  }



  const getUser = (userId, callback = () => void (0)) => {
    const fetch = () => {
      fetchData(`user/${userId}`, (newUser) => {
        if (me.user.token === userId) {
          newUser = processMe(newUser)
        }
        callback(newUser)
      })
    }
    fetch()
  }

  const fetchUser = (userId, callback = () => void (0), refetch = false) => {

    const fetch = () => {
      fetchData(`user/${userId}`, (newUser) => {
        const newU = {}
        if (me.user.token === userId) {
          newUser = processMe(newUser)
        }
        newU[userId] = {
          ...newUser,
          avatar: newUser.avatar ? parseAvatar(newUser.avatar) : newUser.avatar,
          followings: parseDesignInPersonas(newUser.followings),
          followers: parseDesignInPersonas(newUser.followers),
        };

        if (refetch) {
          setCachedUsers(prev => ({ ...prev, ...newU }))
        }
        callback(newUser)
      })
    }

    if (cachedUsers[userId] && !refetch) {
      callback(cachedUsers[userId])
      // console.log('cachedUsers[userId] = ', cachedUsers[userId]);
      // if (!cachedUsers[userId].followings) fetch()

    } else {
      fetch()
    }

  }

  const getAvatar = async (avatarId) => {
    let allAvatars = avatars ? [...avatars] : []
    // const allAvatarsDobs = {}
    // allAvatars.forEach(one => {
    //   allAvatarsDobs[one.avatar.token] = one.avatar
    // })
    // console.log('allAvatarsDobs[avatarId] = ', allAvatarsDobs[avatarId]);
    // return allAvatarsDobs[avatarId]
    allAvatars = allAvatars.filter(one => one.avatar.token === avatarId)
    let foundAvatar = allAvatars.length ? allAvatars[0]['avatar'] : null

    if (!foundAvatar) {
      // Fetch avatar from API if not in cache
      try {
        const newAvatar = await fetchDataPromise(`avatar/${avatarId}`);
        foundAvatar = parseAvatar(newAvatar);
      } catch (error) {
        console.error("Error fetching avatar:", error);
        foundAvatar = null;
      }
    }

    return foundAvatar;
  }



  const fetchDataPromise = (what) => {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/api/${what}`)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          resolve(data);
        })
        .catch(error => {
          console.error("Error fetching: ", error);
          reject(error);
        });
    });
  };



  const fetchData = (what, callback = () => void (0), callbackError = () => void (0)) => {
    fetch(`${API_URL}/api/${what}`)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json();
      })
      .then(data => {
        callback(data)
      })
      .catch(error => {
        callbackError()
        console.error("Error fetching: ", error);
      });
  }


  const processSummary = (sum) => {
    let newSum = JSON.parse(JSON.stringify(sum));


    //me
    // newSum.me.avatar.design = newSum.me.avatar.design['meta']
    //   ? parseNewDesign(newSum.me.avatar.design)
    //   : newSum.me.avatar.design
    newSum.me.avatar = parseAvatar(newSum.me.avatar);

    // newSum.avatars = newSum.avatars.map(one => ({
    //   ...one,
    //   design: one.design['meta']
    //     ? parseNewDesign(one.design)
    //     : one.design
    // }))
    newSum.avatars = newSum.avatars.map(one => parseAvatar(one));

    newSum['followers'] = parseDesignInPersonas(newSum['followers'])
    newSum['followings'] = parseDesignInPersonas(newSum['followings'])

    newSum.pentas = newSum.pentas.map(onePenta => ({
      ...onePenta,
      members: parseDesignInPersonas(onePenta.members)
    }))




    newSum.cached = true;


    // setLocal('summary', newSum)
    setFetchingSummary(false)

    return newSum
  }




  useEffect(() => {
    // console.log('🔴🔴 rendering = ');

    // if (OFFLINE) {
    //   setMe(processMe(summary.me))
    // } else {
    //   // fetchData('me/summary', (meData) => setSummary());
    //   // fetchData('me', (meData) => setMe(processMe(meData)));
    // }

    // if ((summary && summary.cached) || !summary && !fetchingSummary) {
    //   setFetchingSummary(true)
    //   console.log('🔴 summary = ', summary);
    //   // console.log('fetchingSummary = ', fetchingSummary);
    //   fetchData('me/summary', (sum) => setSummary(processSummary(sum)), () => setSummary(0));
    // }
    
    // if (!me) {
      setFetchingSummary(true)
      // console.log('fetchingSummary = ', fetchingSummary);
      fetchData('me', (fetchedMe) => {
        const newMe = processMe(fetchedMe)
        setMe(newMe)
        setLocal(ME_STORAGE_KEY, newMe)
        setFetchingSummary(false)
        setMeReady(true)
      }, () => {
        setMe(0)
        setLocal(ME_STORAGE_KEY, '')
        setFetchingSummary(false)
        setMeReady(true)
        cacheClear()
      });
    // }

  }, []);

  // useEffect(() => {
  //   if (summary) {
  //     setMe(processMe(summary.me, summary))
  //   }
  // }, [summary]); // Depend on `me` to avoid refetching if already set

  // useEffect(() => {
  //   if (!me) { // Only fetch if `me` is not already set
  //     if (OFFLINE) {
  //       setMe(processMe(summary.me))
  //     } else {
  //       fetchData('me/summary', (meData) => setSummary());
  //       // fetchData('me', (meData) => setMe(processMe(meData)));
  //     }
  //   }
  // }, [me]);

  // useEffect(() => {
  //   if (me && summary) {
  //     // loadFriends()
  //     // loadAvatars()
  //     // loadPentas()
  //     loadGroups()

  //     // setFriends(summary.friends)
  //     setFollowings(summary.followings)
  //     setFollowers(summary.followers)

  //     const cacheUsers = (arr => {
  //       let usersObj = {}
  //       arr.forEach(one => usersObj[one.user.token] = one)
  //       return usersObj;
  //     })

  //     const cached = {}
  //     cached[summary.me.user.token] = processMe(summary.me, summary)
  //     setCachedUsers({
  //       ...cached,
  //       ...cacheUsers(summary.followings),
  //       ...cacheUsers(summary.followers)
  //     })
  //     setAvatars(processAvatars(summary.avatars))
  //     setPentasList(summary.pentas)
  //     getGroups()

  //   }
  // }, [me, summary]);


  const cacheUsers = (arr => {
    let usersObj = {}
    arr.forEach(one => {
      if (one?.user?.token) usersObj[one.user.token] = one
    })
    return usersObj;
  })

  // Paint the social tabs from the last known payloads while the API request is
  // still in flight. Whatever the API returns afterwards replaces this entirely.
  useEffect(() => {
    const userToken = me?.user?.token;
    if (!userToken || hydratedRef.current === userToken) return;
    hydratedRef.current = userToken;
    listLoadedFromApiRef.current = {};

    let cancelled = false;
    const hydrate = (list, apply) => {
      cacheRead(`${userToken}:${list}`).then((payload) => {
        if (cancelled || !payload || listLoadedFromApiRef.current[list]) return;
        try {
          apply(payload);
        } catch (error) {
          console.error(`Error hydrating cached ${list}:`, error);
        }
      });
    };

    hydrate('avatars', (payload) => setAvatars(processAvatars(payload)));
    // Offline cache is slim stats rows (or legacy full lists) — never re-parse 300 HD designs.
    hydrate('followings', (payload) => {
      const list = Array.isArray(payload) ? payload : [];
      setFollowings(list.map((one, i) => ({
        ...toStatsOnlyPersona(one),
        _listIndex: typeof one?._listIndex === 'number' ? one._listIndex : i,
      })));
    });
    hydrate('followers', (payload) => {
      const list = Array.isArray(payload) ? payload : [];
      setFollowers(list.map((one, i) => ({
        ...toStatsOnlyPersona(one),
        _listIndex: typeof one?._listIndex === 'number' ? one._listIndex : i,
      })));
    });
    hydrate('pentas', (payload) => fetchPentas(payload));

    return () => { cancelled = true };
  }, [me?.user?.token]);

  useEffect(() => {
    if (!me?.user?.token) return;
    loadAvatars(0, LOAD_BY_DEFAULT, () => void (0), true);
    loadPentas();
    loadFollowingsAll();
    loadFollowersAll();
    loadGroups() //can be re-enabled here if needed in the future
    return () => {
      // Cancel in-flight stats walks when the logged-in user changes.
      statsWalkGenRef.current.followings += 1;
      statsWalkGenRef.current.followers += 1;
    };
  }, [me?.user?.token]);


  useEffect(() => {
    if (me && !cachedUsers[me.user.token]) {
      const cached = {}
      cached[me.user.token] = processMe(me)
      setCachedUsers({
        ...cached
      })
    }
  }, [me]);

  useEffect(() => {
    if (followings?.length && followers?.length) {
      const fullOnly = (arr = []) => arr.filter((one) => one && !one._statsOnly);
      setCachedUsers({
        ...cachedUsers,
        ...cacheUsers(fullOnly(followings)),
        ...cacheUsers(fullOnly(followers))
      })
    }
  }, [followings, followers]);


  const checkIfFollow = (token) => {
    if (!followings) return false;
    return followings.some(one => one?.user?.token === token);
  }


  const unfollow = ({ user, avatar }, callback = () => void (0)) => {
    if (!followings) return false;
    setFollowingUser(prev => [...prev, user.token])
    setFollowings(prev => prev.filter(one => one?.user?.token !== user.token))
    setMe(prev => {
      if (!prev?.user?.aggr) return prev;
      const newMe = {
        ...prev,
        user: {
          ...prev.user,
          aggr: {
            ...prev.user.aggr,
            followings_count: Math.max(0, (prev.user.aggr.followings_count || 0) - 1)
          }
        }
      };
      setLocal('me', newMe);
      return newMe;
    })
    _unfollow(user.token,
      () => {
        const total = Math.max(0, (me?.user?.aggr?.followings_count || 0) - 1);
        loadFollowingsAll(() => setFollowingUser(prev => prev.filter(one => one !== user.token)), total);
        callback()
      })
  }

  const follow = ({ user, avatar }, callback = () => void (0)) => {
    if (!followings) return false;
    setFollowingUser(prev => [...prev, user.token])
    _follow(user.token,
      () => {
        const total = (me?.user?.aggr?.followings_count || 0) + 1;
        loadFollowingsAll(() => setFollowingUser(prev => prev.filter(one => one !== user.token)), total);
        setMe(prev => {
          if (!prev?.user?.aggr) return prev;
          const newMe = {
            ...prev,
            user: {
              ...prev.user,
              aggr: {
                ...prev.user.aggr,
                followings_count: (prev.user.aggr.followings_count || 0) + 1
              }
            }
          };
          setLocal('me', newMe);
          return newMe;
        });
        callback()
      })
  }

  useEffect(() => {

    //AUTOFREIND
    const friendToken = getLocal('invited_by') ? getLocal('invited_by') : '';
    if (me && followings && friendToken) {
      follow({ user: { token: friendToken } })
      // localStorage.setItem('invited_by', '')
      _invitedBy(friendToken)
      localStorage.setItem('invited_by', '')
    }

    //SETME

    const clearImport = () => localStorage.setItem('import_avatar', null)

    const import_avatar = getLocal('import_avatar') ? getLocal('import_avatar') : '';
    if (me
      && !me.avatar.city_id
      && import_avatar
      && import_avatar['dob']
      && import_avatar['city_id']
    ) {

      import_avatar['city_id'] = parseInt(import_avatar['city_id'])

      // if (friendToken) {
      //   import_avatar['invited_by'] = friendToken
      // }

      updateAvatar({
        ...import_avatar,
        'token': me.avatar.token
      }, clearImport)
    }



  }, [me])






  useEffect(() => {
    // console.log('😝')
    // console.log('me = ', me)
    // console.log('myCity = ', myCity)
    // console.log('friends = ', friends)
    // console.log('followings = ', followings)
    // console.log('followers = ', followers)
    // console.log('avatars = ', avatars)
    // console.log('pentas = ', pentas)
    // console.log('groups = ', groups)
    // console.log('cachedDesigns = ', cachedDesigns)
    // console.log('updatingAva = ', updatingAva)
    // console.log('creatingAva = ', creatingAva)
    // console.log('creatingPenta = ', creatingPenta)
    // console.log('loadingDesign = ', loadingDesign)
    // console.log('deletingAvatar = ', deletingAvatar)
    // console.log('followingUser = ', followingUser)
    // console.log('cachedUsers = ', cachedUsers)
    // console.log('geoPeople = ', geoPeople)
    // console.log('myCities = ', myCities)
  }, [
    // me,
    // myCity,
    // friends,
    // followings,
    // followers,
    // avatars,
    // pentas,
    // groups,
    // cachedDesigns,
    // updatingAva,
    // creatingAva,
    // creatingPenta,
    // loadingDesign,
    // deletingAvatar,
    // followingUser,
    // cachedUsers,
    // geoPeople,
    // myCities
  ])

  const updatePenta = (pentaUpdate, callback = () => void (0)) => {
    const {
      members: membersOverride,
      pentaDesign: _pentaDesign,
      my: _my,
      unsaved: _unsaved,
      ...apiPayload
    } = pentaUpdate;

    setUpdatingPenta(apiPayload.token)
    _updatePenta(apiPayload, (newPenta) => {
      setUpdatingPenta()
      const newPentas = JSON.parse(JSON.stringify(pentas));
      const pentaIndex = newPentas.findIndex(one => one.token === newPenta.token)
      if (pentaIndex >= 0) {
        const mergedPenta = {
          ...newPentas[pentaIndex],
          ...newPenta,
          members: membersOverride || newPentas[pentaIndex].members,
        }
        enrichPenta(mergedPenta)
        newPentas[pentaIndex] = mergedPenta
        setPentas(newPentas)
        invalidateList('pentas')
        callback(mergedPenta)
      } else {
        callback(membersOverride ? { ...newPenta, members: membersOverride } : newPenta)
      }
    })
  }

  useEffect(() => {
  }, [me]);

const value = useMemo(() => ({
    me,
    meReady,
    myCity,
    friends,
    followings,
    followers,
    followingsHydrated,
    followersHydrated,
    followingsComplete,
    followersComplete,
    avatars,
    pentas,
    groups,
    cachedDesigns,
    updatingAva,
    updatingPenta,
    creatingAva,
    creatingPenta,
    loadingDesign,
    deletingAvatar,
    followingUser,
    cachedUsers,
    geoPeople,
    cityPeople,
    myCities,
    publicUser
  }), [me, meReady, myCity, avatars, pentas, groups, followings, followers, followingsHydrated, followersHydrated, followingsComplete, followersComplete, friends, cachedDesigns, updatingAva, updatingPenta, creatingAva, creatingPenta, loadingDesign, deletingAvatar, followingUser, cachedUsers, geoPeople, myCities, publicUser]);

  return (
    <SocialContext.Provider
      value={
        {
          ...value,
          x: {
            fetchDataAsync,
            refreshMe,
            updateMyCity,
            getCityPeople,
            unfollow,
            follow,
            fetchPentaAvatars,
            getDesign,
            setIsMe,
            getAvatar,
            getUser,
            getUserSocial,
            fetchUser,
            updateAvatar,
            updatePenta,
            setUpdatingAva,
            createAvatar,
            deleteAvatar,
            createPenta,
            createPentaWithMe,
            checkIfFollow,
            addCityHistory,
            fetchAllUsers,
            getPenta,
            fetchPenta,
            addToPenta,
            deletePenta,
            deleteFromPenta,
            setPublicUser,
            getGroups,
            createGroup,
            deleteGroup,
            removeUserFromGroup,
            leaveGroup,
            updateGroup,
            loadFollowings,
            loadFollowers,
            loadAvatars
          }
        }
      }
    >
      {children}

      {/* {summary !== 0 && me && avatars && pentas && followings && followers && children}
      {summary !== 0 && !me &&
        <>
          <PreloaderHD />
        </>
      }
      {summary === 0 && children} */}

      {/* {(me === 0 || typeof me === 'object') && children}
      {typeof me == 'undefined' &&
        <>
          <PreloaderHD />
        </>
      } */}

      {/* {summary === 0 && children} */}
    </SocialContext.Provider>
  )
}


const SocialConsumer = SocialContext.Consumer;

export { SocialConsumer, SocialContext, SocialProvider };

